import { useEffect, useRef, useState } from 'react';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

export default function Process() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps: ProcessStep[] = [
    {
      number: '01',
      title: 'Discover',
      description: 'Understanding your goals, audience, and challenges. Deep research into your market, competitors, and opportunities. This phase is about asking the right questions and uncovering insights that will guide every decision.',
    },
    {
      number: '02',
      title: 'Design',
      description: 'Translating insights into visual solutions. Wireframes, prototypes, and design systems come together. Every element is crafted with purpose, tested for usability, and refined for impact.',
    },
    {
      number: '03',
      title: 'Build',
      description: 'Bringing designs to life with clean, efficient code. Responsive development, performance optimization, and seamless integrations. The focus is on creating experiences that work flawlessly across all devices.',
    },
    {
      number: '04',
      title: 'Refine',
      description: 'Testing, iterating, and perfecting. User feedback drives improvements. Analytics reveal opportunities. The work is never truly done—it evolves with your needs and your audience.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const scrollProgress = entry.intersectionRatio;
            const stepIndex = Math.min(
              Math.floor(scrollProgress * steps.length),
              steps.length - 1
            );
            setActiveStep(stepIndex);
          }
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <section id="process" ref={sectionRef} className="relative py-32 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2
          className="text-5xl md:text-6xl font-bold text-black mb-20 leading-tight text-center"
          style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
        >
          How I Think
        </h2>

        <div className="space-y-24">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative transition-all duration-700"
              style={{
                opacity: activeStep >= index ? 1 : 0.3,
                transform: activeStep >= index ? 'translateY(0)' : 'translateY(50px)',
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-3">
                  <span
                    className="text-8xl font-bold text-black/10"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {step.number}
                  </span>
                </div>

                <div className="lg:col-span-9">
                  <div className="backdrop-blur-xl bg-white/60 border border-white/80 rounded-3xl p-10 transition-all duration-500 hover:bg-white/80 hover:shadow-xl">
                    <h3
                      className="text-4xl font-bold text-black mb-6"
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
                    >
                      {step.title}
                    </h3>

                    <p
                      className="text-xl text-black/70 leading-relaxed"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>

              {index < steps.length - 1 && (
                <div className="flex justify-center my-12">
                  <div
                    className="w-px h-16 bg-gradient-to-b from-black/20 to-transparent transition-all duration-700"
                    style={{
                      opacity: activeStep > index ? 1 : 0.2,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
